import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {TranslocoService} from '@jsverse/transloco';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {GlobalService} from 'app/global/global.service';
import {DocumentFieldService} from '../documentfield.service';
import {IIMetadataItemOptionsDto, IMetadataItemDto, MetaDataTypeEnum} from '../metadata-item.dto';
import { MetaDataSectionEnum, sections } from '../meta-data.helper';
import { JsonFileDTO } from './JsonFileDTO.dto';

interface IDropListDTO {
  value: string
  label: string
}


@Component({
  selector: 'app-meta-data-form',
  templateUrl: './meta-data-form.component.html',
  styleUrl: './meta-data-form.component.scss'
})
export class MetaDataFormComponent implements OnInit, OnChanges {
 
 
  private meta_name: string;
  private placeholder: string;
  private meta_type: string = MetaDataTypeEnum.text;
  private meta_type_disabled: boolean;
  private add_meta_data_options: IIMetadataItemOptionsDto[] = [];
 
 
  @Output() documentFieldsInit: EventEmitter<void> = new EventEmitter();
  @Output() toggleSidebar: EventEmitter<{name: string}> = new EventEmitter();
  @Input() document_type_custom_boxe_id: number = null;
  @Input() meta_data_section_hidden: boolean = false;
  @Input() section: string = '';


  @Input() get meta_data_name(): string {
    return this.meta_name;
  }
  
  set meta_data_name(value: string) {
    this.meta_name = value || null;
  }

  @Input() get meta_data_placeholder(): string {
    return this.placeholder;
  }
  
  set meta_data_placeholder(value: string) {
    this.placeholder = value || null;
  }
  
  @Input('meta_data_type') get meta_data_type() {
    return this.meta_type;
  }
  
  set meta_data_type(value) {
    this.meta_type = value || MetaDataTypeEnum.text;
  }

  @Input('disabled_meta_data_type') get disabled_meta_data_type(): boolean {
    return this.meta_type_disabled;
  }

  set disabled_meta_data_type(value: boolean) {
    this.meta_type_disabled = value || false;
  }

  @Input('options') get options(): IIMetadataItemOptionsDto[] {
    return this.add_meta_data_options;
  }
  
  set options(value: IIMetadataItemOptionsDto[]) {
    this.add_meta_data_options = value || [];
  }
  
  @Input() custom_box_field: boolean = false;
  @Input() view: string = 'meta_data';
  @Input() documentField: IMetadataItemDto;

  public documentFieldForm: FormGroup;
  public readonly: boolean = false;
  public searchable: boolean = false;
  public submitted: boolean = false
  public changesDetect: boolean = false;
  public loading: boolean = false;

  public includeOrFieldtype: IDropListDTO[] = [
    { value: MetaDataTypeEnum.text, label: this._translocoService.translate('Meta data field type droplist option - (Meta data section) - Text') },
    { value: MetaDataTypeEnum.checkbox, label: this._translocoService.translate('Meta data field type droplist option - (Meta data section) - Checkbox') },
    { value: MetaDataTypeEnum.droplist, label: this._translocoService.translate('Meta data field type droplist option - (Meta data section) - Multiple choice') },
    { value: MetaDataTypeEnum.datepicker, label: this._translocoService.translate('Meta data field type droplist option - (Meta data section) - Date picker') },
    { value: MetaDataTypeEnum.image, label: this._translocoService.translate('Meta data field type image - (Meta data section) - Image') },
  ];

  public editValidation: IDropListDTO[] = [
    { value: 'numbers', label: this._translocoService.translate('Meta data field validation type droplist option - (Meta data section) - Numbers') },
    { value: 'letters', label: this._translocoService.translate('Meta data field validation type droplist option - (Meta data section) - Letters') },
    { value: 'numbers_letters', label: this._translocoService.translate('Meta data field validation type droplist option - (Meta data section) - Numbers and Letters') },
    { value: 'email', label: this._translocoService.translate('Meta data field validation type droplist option - (Meta data section) - Email') },
    { value: 'custom_regex_validation', label: this._translocoService.translate('Meta data field validation type droplist option - (Meta data section) - Custom Validation') }
  ];

  public sections: IDropListDTO[] = sections().filter(section => section.value !== "");
  public metaDataSectionEnum = MetaDataSectionEnum;

  public numberValidationtype: IDropListDTO[] = [
    { value: 'numbers_range', label: this._translocoService.translate('Meta data field number range validation droplist option - (Meta data section) - Number range') },
    { value: 'numbers_characters', label: this._translocoService.translate('Meta data field number range validation droplist option - (Meta data section) - Number of characters') },
  ];

  public droplistJsonFileName: string = '';

  /**
   * Constructor
   * @param formBuilder 
   * @param documentFieldService 
   * @param globalService 
   * @param activeModal 
   * @param _translocoService 
   */
  constructor(
    private formBuilder: FormBuilder,
    private documentFieldService: DocumentFieldService,
    private globalService: GlobalService,
    private activeModal: NgbActiveModal,
    private _translocoService: TranslocoService
  ) { }

  ngOnInit(): void {
    this.initForm({} as IMetadataItemDto);
  }



  /**
   * Detect changes in component
   * @param change 
   */
  ngOnChanges(change: SimpleChanges): void {
    if(change){
      this.initForm(this.documentField);
    }
  }



  /**
   * fetch form controls
   */
  get form() {
    return this.documentFieldForm.controls;
  }


  /**
   * Init form with data object 
   * @param data 
   */
  initForm(data: IMetadataItemDto) {
    this.droplistJsonFileName = '';
    this.documentFieldForm = this.formBuilder.group({
      id: [data?.id ?? ''],
      meta_data_name: [data?.meta_data_name ?? this.meta_name, [Validators.required]],
      meta_data_type: [{value:data?.meta_data_type ?? this.meta_type, disabled:this.meta_type_disabled}, [Validators.required]],
      placeholder: [data?.placeholder ?? this.placeholder],
      section: [(data?.section ?? this.section) || 'document_field'],
      validation_type: [(data?.field_validations ? data?.field_validations.validation_type : null)],
      number_validation_type: [(data?.field_validations ? data?.field_validations.number_validation_type : null)],
      min: [(data?.field_validations ? data?.field_validations.min : null)],
      max: [(data?.field_validations ? data?.field_validations.max : null)],
      decimals: [(data?.field_validations ? data?.field_validations.decimals : null)],
      regular_expression: [(data?.field_validations ? data?.field_validations.regular_expression : null)],
      validation_help_text: [(data?.field_validations ? data?.field_validations.validation_help_text : null)],
      api_attribute: [data?.api_attribute ?? ''],
      hint: [data?.hint ?? ''],
      multi_select: [data?.multi_select ?? false],
      multiple_line_field: [data?.multiple_line_field ?? false],
      meta_data_description: [data?.meta_data_description ?? ''],
      options: this.formBuilder.array([]),
      document_type_custom_boxe_id: [this.custom_box_field ? this.document_type_custom_boxe_id : 0],
      custom_box_field: [this.custom_box_field],
      json_options: [[]],
      api_field: [data?.api_field ?? false],
      api_url: [data?.api_url ?? ''],
      api_key: [data?.api_key ?? ''],
      expire_days: [data?.expire_days ?? null]
    });

    /** On meta data type change */
    this.documentFieldForm.get('meta_data_type').valueChanges.subscribe((value: MetaDataTypeEnum) => {
      if (value !== MetaDataTypeEnum.text) {
        this.documentFieldForm.patchValue(
          {
            validation_type: null,
            number_validation_type: null,
            min: null,
            max: null,
            decimal_value: null,
            total_characters: null,
            regular_expression: null,
            validation_helper_text: null,
          }
        );
        if (value === MetaDataTypeEnum.image) {
          const fieldSection = this.documentFieldForm.get('section')?.value;
          if(fieldSection !== 'person_field' && fieldSection !== 'org_field'){
            this.documentFieldForm.patchValue({ section: 'person_field' });
          }
        }
      }
      if (value === MetaDataTypeEnum.droplist) {
        this.addOption();
      } else {
        this.optionsControl.clear();
      }
      this.toggleApiValidators();
    });

    /** On section dropdown change */
    this.documentFieldForm.get('section')?.valueChanges.subscribe((section: string) => {
      const fieldType = this.documentFieldForm.get('meta_data_type')?.value;

      const isPersonOrOrg = section === 'person_field' || section === 'org_field';
      const isDocumentField = section === 'document_field';

      // Only allow "image" if it's a person/org section
      if (fieldType === MetaDataTypeEnum.image && !isPersonOrOrg) {
        this.documentFieldForm.patchValue(
          { meta_data_type: MetaDataTypeEnum.text },
          { emitEvent: false }
        );
      }

      // For all other cases except document_field, enforce text type
      else if (fieldType !== MetaDataTypeEnum.text && !isDocumentField) {
        this.documentFieldForm.patchValue(
          { meta_data_type: MetaDataTypeEnum.text },
          { emitEvent: false }
        );
      }

      this.toggleApiValidators();
    });

    if(this.add_meta_data_options.length){
      data['meta_data_option'] = this.add_meta_data_options;
    }
    /** Push dropdown options with formGroup */
    if (data && data?.meta_data_option?.length) {
      for (const option of data.meta_data_option) {
        this.addOption(option);
      }
    }

    // Subscribe to changes in api_field
    this.documentFieldForm.get('api_field')?.valueChanges.subscribe(() => {
      this.toggleApiValidators();
    });

    // Run once on init with current value
    this.toggleApiValidators();
  }

  toggleApiValidators(): void {
    const fieldType = this.documentFieldForm.get('meta_data_type')?.value;
    const isAPIField = this.documentFieldForm.get('api_field')?.value;
    const apiUrlControl = this.documentFieldForm.get('api_url');
    const apiKeyControl = this.documentFieldForm.get('api_key');
    const fieldSection = this.documentFieldForm.get('section').value;
  
    if (fieldType === MetaDataTypeEnum.text && isAPIField && fieldSection === 'document_field') {
      apiUrlControl?.setValidators([Validators.required, Validators.pattern(/https?:\/\/[\w\\-]+(\.[\w\\-]+)+[/#?]?.*$/)]);
      apiKeyControl?.setValidators([Validators.required]);
      this.documentFieldForm.get('multiple_line_field')?.setValue(false);
    } else {
      apiUrlControl?.clearValidators();
      apiKeyControl?.clearValidators();
      if(isAPIField){
        this.documentFieldForm.get('api_field')?.setValue(false);
      }
      this.documentFieldForm.get('api_url')?.setValue('');
      this.documentFieldForm.get('api_key')?.setValue('');
    }
  
    apiUrlControl?.updateValueAndValidity();
    apiKeyControl?.updateValueAndValidity();
  }

  /**
   * Get options control
   */
  get optionsControl() {
    return this.documentFieldForm.controls["options"] as FormArray;
  }



  /**
   * When we select droplist from document field type then patching options on first time
   * @param $event 
   */
  selectedType($event: {value: string}) {
    if ($event?.value == 'droplist') {
      this.addOption();
    } else {
      this.optionsControl.clear();
    }
  }



  /**
   * Append droplist options
   * @param data
   */
  addOption(data?: IIMetadataItemOptionsDto) {
    const option = this.formBuilder.group({
      id: data?.id ?? null,
      name: data?.name ?? null,
      value: data?.value ?? '',
      show: data?.value ? 1 : 0,
    });
    option.get('name').setValidators([Validators.required]);
    option.get('name').updateValueAndValidity();
    this.optionsControl.push(option);
  }



  /**
   * Removing options control behalf on index
   * @param i 
   */
  removeOption(i: number) {
    this.optionsControl.removeAt(i);
  }


  /**
   * Toggle option value field with index and value
   * @param i 
   * @param value 
   */
  toggleValueField(i: number, value: number) {
    this.optionsControl.controls[i].patchValue({ value: '', show: value });
  }



  /**
   * Create or update document fields
   * @returns 
   */
  createOrUpdateFields() {
    this.submitted = true;
    if (this.form.api_attribute.value == '') {
      this.documentFieldForm.patchValue({ api_attribute: this.form?.meta_data_name?.value?.replace(/[^A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż0-9.,?!@#&()\-'"%;:^$]/g, '_') })
    } else {
      this.documentFieldForm.patchValue({ api_attribute: this.form?.api_attribute?.value?.replace(/[^A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż0-9.,?!@#&()\-'"%;:^$]/g, '_') })
    }
    if (this.documentFieldForm.invalid) return;

    this.loading = true;
    this.form?.id?.value == '' ? this.createDocumentField()
     : this.updateDocumentField();
  }



  /**
   * Creating document field
   * @returns 
   */
  createDocumentField() {
    if(this.optionsControl.length == 0 && this.documentFieldForm.get('json_options')?.value.length == 0 && this.documentFieldForm?.get('meta_data_type')?.value === 'droplist'){
      this.loading = false;
      this.globalService.toastrError(this._translocoService.translate('Meta data empty droplist options toaster message - (Meta data section) - Please add at least one options'));
      return;
    }
    this.documentFieldService.createDocumentField(this.documentFieldForm.getRawValue()).subscribe({
      next: (documentfield: IMetadataItemDto) => {
        this.submitted = false;
        this.loading = false;
        this.globalService.toastrSuccess(this._translocoService.translate('Meta data section - (Meta data section) - Create meta data request toast message'));
        this.documentFieldForm.reset();
        this.optionsControl.clear();
        this.documentFieldsInit.emit();
        this.closeSidebar(documentfield);
      },

      error: (error) => {
        this.submitted = false;
        this.loading = false;
        this.globalService.toastrError(error);
      }
    });
  }



  /** Close sidebar with emitter */
private closeSidebar(metaData?: IMetadataItemDto){
    this.submitted = false;
    this.documentFieldForm.reset();
    if(this.view === 'modal'){
      this.activeModal.close(metaData);
    } else {
      this.toggleSidebar.emit({name: 'document-field-sidebar'});
    }
  }




  /**
   * Update document field
   * @returns 
   */
  updateDocumentField() {
    this.documentFieldService.updateDocumentField(this.documentFieldForm.getRawValue()).subscribe({
      next: () => {
        this.submitted = false;
        this.loading = false;
        this.globalService.toastrSuccess(this._translocoService.translate('Meta data section - (Meta data section) - Update meta data request toast message'));
        this.documentFieldForm.reset();
        this.optionsControl.clear();
        this.documentFieldsInit.emit();
        this.closeSidebar();
      },

      error: (error) => {
        this.submitted = false;
        this.loading = false;
        this.globalService.toastrError(error);
      }
    });
  }

  private isValidJsonFile(data: unknown): data is JsonFileDTO {
    if (typeof data !== 'object' || data === null) return false;
  
    const file = data as JsonFileDTO;
    if (!Array.isArray(file.result)) return false;
  
    return file.result.every(root =>
      typeof root.option_name === 'string' &&
      typeof root.alternate_name === 'string' &&
      (root.childs === undefined || (
        Array.isArray(root.childs) &&
        root.childs.every(job =>
          typeof job.option_name === 'string' &&
          typeof job.alternate_name === 'string' &&
          (job.childs === undefined || (
            Array.isArray(job.childs) &&
            job.childs.every(task =>
              typeof task.option_name === 'string' &&
              typeof task.alternate_name === 'string'
            )
          ))
        )
      ))
    );
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files?.length) return;

    const file: File = input.files[0];
    this.droplistJsonFileName = file.name;

    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;

      try {
        const parsed: unknown = JSON.parse(text);

        if (this.isValidJsonFile(parsed)) {
          this.optionsControl.clear();
          this.documentFieldForm.get('json_options')?.setValue(parsed.result);
        } else {
          this.globalService.toastrError(this._translocoService.translate('Meta data invalid droplist json options toaster message (Meta data section) - Invalid JSON structure'));
        }
      } catch (error) {
        this.globalService.toastrError(this._translocoService.translate('Meta data invalid droplist json options toaster message (Meta data section) - Invalid JSON structure'));
      }
    };

    reader.readAsText(file);
  }
}
